import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash.debounce';

import { FREE_PREDICTIONS_GAMEWEEK } from './constants';
import { MAX_GAMEWEEK } from '@lib/constants';
import { PlayerKit } from '@components/player/player-kit';
import CheckBox from '@components/atoms/checkbox';
import CostSlider from '@components/fpl-ui/cost-slider';
import Icon from '@components/atoms/icon';
import SearchInput from '@components/SearchInput';
import { getPredictedPointsLevelStyles } from '@components/fixtures/utils/fixtureTypes';
import {
  BodyRegular,
  BodySmallRegular,
  BodySmallRegularBold,
  CaptionBold,
  H3
} from '@components/atoms/typography';
import {
  type AvailabilitySeverity,
  getAvailabilityLabel,
  getAvailabilitySeverity
} from '@lib/my-team/player-availability';
import { useAccessToken } from '@hooks/useAccessToken';
import { useAuth0User } from '@hooks/useAuth0User';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { usePlayers } from '@hooks/usePlayers';
import { useTransferSuggestions } from '@hooks/my-team/useTransferSuggestions';
import type {
  Player,
  PlayerFixture,
  PlayerPosition,
  PlayerSortBy
} from '@api-service/players';

type PositionFilter = PlayerPosition | 'ALL';

const POSITION_OPTIONS: { label: string; value: PositionFilter }[] = [
  { label: 'All', value: 'ALL' },
  { label: 'GK', value: 'GK' },
  { label: 'DEF', value: 'DEF' },
  { label: 'MID', value: 'MID' },
  { label: 'FWD', value: 'FWD' }
];

type SortOption = {
  key: string;
  label: string;
  value: PlayerSortBy;
  abbr: string;
  horizon?: 1 | 3;
};

const SORT_OPTIONS: SortOption[] = [
  {
    key: 'predicted-1',
    label: 'Predicted points (next GW)',
    value: 'predictedPoints',
    abbr: 'PP',
    horizon: 1
  },
  {
    key: 'predicted-3',
    label: 'Predicted points (next 3 GWs)',
    value: 'predictedPoints',
    abbr: 'Total',
    horizon: 3
  },
  {
    key: 'total-points',
    label: 'Total points',
    value: 'totalPoints',
    abbr: 'TP'
  },
  {
    key: 'points-per-start',
    label: 'Points per start',
    value: 'pointsPerStart',
    abbr: 'PPS'
  },
  { key: 'ownership', label: 'Ownership %', value: 'ownership', abbr: 'OWN' },
  { key: 'value', label: 'Value', value: 'value', abbr: 'VAL' },
  { key: 'price', label: 'Price', value: 'price', abbr: 'PRC' }
];

const DEFAULT_TILE_COUNT = 3;
const SEARCH_DEBOUNCE_MS = 300;
const PAGE_LENGTH = 10;
const SEARCH_MIN_LENGTH = 3;
const IN_TEAM_LABEL = 'In your team';

// The info button leads each row, so the header needs a spacer of the same
// width or the column headings drift off the values beneath them.
const INFO_COLUMN_CLASS = 'w-6 flex-none';
// Row text colour per availability severity: amber for a mild doubt, red for
// anything worse. Mirrors the pitch tile name row.
const AVAILABILITY_TEXT_COLOURS: Record<
  AvailabilitySeverity,
  string | undefined
> = {
  none: undefined,
  caution: 'text-warning-600',
  danger: 'text-danger-600'
};

// The longest fixture label ("WHU (H)") measures 35.8px in Open Sans at
// text-[9px], so 41px clears it with the tile's own px-0.5 either side. The
// tiles are fixed to this width and refuse to shrink, so the stat columns stay
// aligned and legible however tight the panel gets: the player name column
// gives up space instead. The skeleton block below is sized from the same
// constant so it cannot drift from the real tiles.
const FIXTURE_TILE_WIDTH_IN_PX = 41;

const formatCost = (price: number) => `£${price.toFixed(1)}m`;
const formatSortValue = (value: number) => value.toFixed(1);

export const roundToOneDecimalPlace = (value: number) =>
  Math.round(value * 10) / 10;

// Anchor the visible fixtures at the viewed gameweek rather than trusting the
// response order, so a stale or over-broad window can't misalign the tiles.
// Without an anchor (league context still loading) the response order is all
// there is to go on.
const getWindowFixtures = (
  fixtures: PlayerFixture[],
  startGameweek: number | undefined,
  tileCount: number
) =>
  (startGameweek == null
    ? fixtures
    : fixtures.filter((fixture) => fixture.gameweek >= startGameweek)
  ).slice(0, tileCount);

const formatWindowLabel = (
  startGameweek: number | undefined,
  endGameweek: number | undefined,
  tileCount: number
) => {
  if (startGameweek == null || endGameweek == null) {
    return tileCount === 1 ? 'Next GW' : `Next ${tileCount} GWs`;
  }
  return endGameweek === startGameweek
    ? `GW ${startGameweek}`
    : `GW ${startGameweek}-${endGameweek}`;
};

// Sum the per-gameweek values as displayed (rounded to 1dp) so the total
// always equals what the fixture tiles show.
const sumFixturePoints = (
  fixtures: PlayerFixture[],
  startGameweek: number | undefined,
  horizon: number
) =>
  getWindowFixtures(fixtures, startGameweek, horizon).reduce(
    (total, fixture) =>
      total + roundToOneDecimalPlace(getFixturePoints(fixture) ?? 0),
    0
  );

const getSortDisplayValue = (
  player: Player,
  sort: SortOption,
  startGameweek: number | undefined
): number => {
  if (sort.value === 'price') return player.price;
  if (sort.value === 'totalPoints')
    return player.seasonStats?.points?.total ?? 0;
  if (sort.value === 'ownership') return player.ownership ?? 0;
  if (sort.value === 'pointsPerStart') {
    return player.aggregates?.pointsPerStart ?? 0;
  }
  if (sort.value === 'predictedPoints') {
    return sumFixturePoints(player.fixtures, startGameweek, sort.horizon ?? 1);
  }
  if (sort.value === 'value') {
    const points = player.seasonStats?.points?.total ?? 0;
    return player.price > 0 ? points / player.price : 0;
  }
  return 0;
};

const getFixturePoints = (fixture: PlayerFixture): number | null =>
  fixture.predictions?.points ?? null;

type FixtureTileProps = {
  fixture: PlayerFixture;
  isFirst: boolean;
  isLast: boolean;
  isPredictionLocked?: boolean;
};

const FixtureTile = ({
  fixture,
  isFirst,
  isLast,
  isPredictionLocked = false
}: FixtureTileProps) => {
  const points = getFixturePoints(fixture);
  const tileStyles = isPredictionLocked
    ? 'bg-neutral-100 text-neutral-700'
    : getPredictedPointsLevelStyles(points ?? 0);
  return (
    <div
      className={classNames(
        'flex flex-col items-center justify-center px-0.5 py-0.5 flex-none',
        tileStyles,
        isFirst && 'rounded-l-md',
        isLast && 'rounded-r-md'
      )}
      style={{ width: `${FIXTURE_TILE_WIDTH_IN_PX}px` }}
      data-cy="player-list-fixture"
    >
      {isPredictionLocked ? (
        <span
          className="leading-tight"
          data-cy="player-list-fixture-lock"
          aria-label="Predicted points are for members only"
        >
          <Icon name="Lock" fontSize="12px" />
        </span>
      ) : (
        <span className="text-xs font-semibold leading-tight whitespace-nowrap">
          {points == null ? '-' : points.toFixed(1)}
        </span>
      )}
      <span className="text-[9px] leading-tight whitespace-nowrap">
        {fixture.opponent.shortName}
        {fixture.isHome ? ' (H)' : ' (A)'}
      </span>
    </div>
  );
};

type PlayerRowProps = {
  player: Player;
  sort: SortOption;
  startGameweek: number | undefined;
  tileCount: number;
  onSelectPlayer?: (player: Player) => void;
  onShowPlayerInfo?: (player: Player) => void;
  arePredictionsLocked?: boolean;
  /**
   * The player is already spoken for (in the squad, or staged in a transfer),
   * so the row is shown for reference but cannot be picked.
   */
  isDisabled?: boolean;
};

const PlayerRow = ({
  player,
  sort,
  startGameweek,
  tileCount,
  onSelectPlayer,
  onShowPlayerInfo,
  arePredictionsLocked = false,
  isDisabled = false
}: PlayerRowProps) => {
  const visibleFixtures = getWindowFixtures(
    player.fixtures,
    startGameweek,
    tileCount
  );
  const isSelectable = Boolean(onSelectPlayer) && !isDisabled;
  const Wrapper = isSelectable ? 'button' : 'div';
  // Flag doubts and injuries on the row itself, so an unavailable player is
  // obvious in the list without opening them. Mirrors the pitch tiles.
  const availabilitySeverity = getAvailabilitySeverity(
    player.status,
    player.chanceOfPlaying
  );
  const isUnavailable = availabilitySeverity !== 'none';
  const availabilityTextColor = AVAILABILITY_TEXT_COLOURS[availabilitySeverity];
  const availabilityLabel = getAvailabilityLabel(
    player.status,
    player.chanceOfPlaying
  );

  // The info button is a sibling of the row rather than a child: when the row
  // is selectable it renders as a button, and a button cannot nest a button.
  // It leads the row so the price, fixture and sort columns stay under their
  // headings.
  return (
    <div className="flex items-center gap-1 border-b border-neutral-100 last:border-b-0">
      {onShowPlayerInfo && (
        <button
          type="button"
          onClick={() => onShowPlayerInfo(player)}
          aria-label={`Player info for ${player.displayName}`}
          title={`Player info for ${player.displayName}`}
          className={`${INFO_COLUMN_CLASS} flex items-center justify-center py-2 rounded text-neutral-400 hover:text-primary-600 hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary`}
          data-cy="player-list-item-info"
          data-cy-player-id={player.id}
        >
          <Icon name="InfoFilled" aria-hidden className="w-4 h-4" />
        </button>
      )}
      <Wrapper
        type={isSelectable ? 'button' : undefined}
        onClick={
          isSelectable && onSelectPlayer
            ? () => onSelectPlayer(player)
            : undefined
        }
        aria-disabled={isDisabled || undefined}
        className={classNames(
          'flex items-center justify-between gap-2 py-2 px-1 min-w-0 flex-1 text-left',
          isSelectable && 'cursor-pointer hover:bg-primary-50 rounded',
          isDisabled && 'opacity-40 cursor-not-allowed'
        )}
        data-cy="player-list-item"
        data-cy-player-id={player.id}
        data-cy-in-team={isDisabled ? 'true' : 'false'}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-8 h-10 flex-none flex items-center justify-center">
            <PlayerKit
              teamShortName={player.team.shortName}
              kitImageUrl={player.kitImageUrl}
              className="max-h-full w-auto object-contain"
            />
          </div>
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-1 min-w-0">
              {isUnavailable && (
                <Icon
                  name="Warning"
                  title={availabilityLabel ?? undefined}
                  aria-label={availabilityLabel ?? 'Availability warning'}
                  className={classNames(
                    'w-3 h-3 shrink-0',
                    availabilityTextColor
                  )}
                />
              )}
              <BodySmallRegularBold
                className="truncate"
                textColor={availabilityTextColor}
                data-cy="player-list-item-name"
                data-cy-unavailable={isUnavailable ? 'true' : 'false'}
                data-cy-availability={availabilitySeverity}
              >
                {player.displayName}
              </BodySmallRegularBold>
            </div>
            <CaptionBold textColor="text-neutral-600">
              {player.team.shortName} - {player.position}
              {isUnavailable && availabilityLabel
                ? ` · ${availabilityLabel}`
                : ''}
              {/* Says why the row is dimmed, so a greyed player doesn't read
              as a rendering fault. */}
              {isDisabled ? ` · ${IN_TEAM_LABEL}` : ''}
            </CaptionBold>
          </div>
        </div>
        {/* Sized to its content rather than a fixed width: "£15.5m" needs
        48.7px where "£4.0m" needs 40.7px, and a fixed box narrow enough for the
        common case grew to min-content on premium players and swallowed the gap
        before the fixture tiles. Everything to the right of this is a fixed
        width, so the right edges still line up as a column. */}
        <BodySmallRegular className="text-right flex-none whitespace-nowrap">
          {formatCost(player.price)}
        </BodySmallRegular>
        <div
          className="flex items-center justify-center flex-none rounded-md"
          style={{ width: `${tileCount * FIXTURE_TILE_WIDTH_IN_PX}px` }}
        >
          {visibleFixtures.length > 0 ? (
            visibleFixtures.map((fixture, index) => (
              <FixtureTile
                key={`${fixture.opponent.shortName}-${index}`}
                fixture={fixture}
                isFirst={index === 0}
                isLast={index === visibleFixtures.length - 1}
                isPredictionLocked={
                  arePredictionsLocked &&
                  fixture.gameweek !== FREE_PREDICTIONS_GAMEWEEK
                }
              />
            ))
          ) : (
            <span className="text-xs text-neutral-500">-</span>
          )}
        </div>
        <BodySmallRegularBold className="w-9 text-center flex-none">
          {formatSortValue(getSortDisplayValue(player, sort, startGameweek))}
        </BodySmallRegularBold>
      </Wrapper>
    </div>
  );
};

// Placeholder with the same dimensions as a PlayerRow (the h-10 kit slot
// drives the row height), so the list and the pagination footer below it
// keep their position while rows load.
const PlayerRowSkeleton = ({
  tileCount,
  hasInfoColumn
}: {
  tileCount: number;
  hasInfoColumn: boolean;
}) => (
  <div
    className="flex items-center gap-1 py-2 border-b border-neutral-100 last:border-b-0 w-full animate-pulse"
    data-cy="player-list-skeleton"
  >
    {hasInfoColumn && <span aria-hidden className={INFO_COLUMN_CLASS} />}
    <div className="flex items-center justify-between gap-2 px-1 flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div className="w-8 h-10 flex-none rounded bg-neutral-100" />
        <div className="flex flex-col gap-1 min-w-0">
          <div className="h-3 w-20 rounded bg-neutral-100" />
          <div className="h-2 w-12 rounded bg-neutral-100" />
        </div>
      </div>
      <div className="h-3 w-10 flex-none rounded bg-neutral-100" />
      <div
        className="h-9 flex-none rounded-md bg-neutral-100"
        style={{ width: `${tileCount * FIXTURE_TILE_WIDTH_IN_PX}px` }}
      />
      <div className="h-3 w-9 flex-none rounded bg-neutral-100" />
    </div>
  </div>
);

type PlayerListPanelProps = {
  /**
   * Gameweek the prediction columns are anchored to: the gameweek being
   * viewed on the pick page, so the columns show the same gameweeks as the
   * team players' fixtures. Falls back to the league's next gameweek when
   * omitted (the build flow, which has no gameweek navigation).
   */
  selectedGameweek?: number;
  bank: number;
  onSelectPlayer?: (player: Player) => void;
  /** Opens the player info modal for a row. Omitted hides the info button. */
  onShowPlayerInfo?: (player: Player) => void;
  /**
   * Positions the replacement pick is restricted to. A single entry locks
   * the filter to that position. Multiple entries (transfers staged across
   * different positions, FFH-2088) keep the filter usable: the user can view
   * all of the relevant positions together or narrow to one of them.
   */
  requiredPositions?: PlayerPosition[] | null;
  /**
   * Players who are already spoken for: the current squad, plus anyone staged
   * in a transfer. They stay in the list, but while a replacement is being
   * picked their rows are dimmed and unselectable. They are also kept out of
   * the recommended box below, which only exists to surface players the user
   * could actually bring in.
   */
  disabledPlayerIds?: string[];
  /**
   * Budget available for the replacement, i.e. the projected bank once the
   * outgoing player is sold. Scopes both the recommended players and the
   * "Affordable only" filter to what the user can actually afford. Falls back
   * to `bank` when not picking a replacement.
   */
  replacementBudget?: number;
  /**
   * Padlocks prediction insights for non-paying members: fixture tiles
   * outside the free gameweek show a padlock instead of predicted points,
   * prediction-based sorts are removed (price is the default), and
   * replacement suggestions are disabled.
   */
  arePredictionsLocked?: boolean;
};

export const PlayerListPanel = ({
  selectedGameweek,
  bank,
  onSelectPlayer,
  onShowPlayerInfo,
  requiredPositions,
  disabledPlayerIds,
  replacementBudget,
  arePredictionsLocked = false
}: PlayerListPanelProps) => {
  const { user, isLoading: isUserLoading } = useAuth0User();
  const { accessToken, accessTokenIsFetching } = useAccessToken();
  // Hold the player queries until auth settles: firing before the token is
  // ready sends an anonymous request the API rejects (predictions require
  // auth), wasting a round trip on every page load. The token fetch only
  // runs for logged-in users, so anonymous visitors must not wait on it —
  // accessTokenIsFetching never resolves for them.
  const isAwaitingAccessToken =
    isUserLoading || (Boolean(user) && accessTokenIsFetching);
  const { context } = useLeagueContext();
  const priceRange = context?.playerPriceRange ?? { min: 0, max: 14 };

  const sortOptions = arePredictionsLocked
    ? SORT_OPTIONS.filter((option) => option.value !== 'predictedPoints')
    : SORT_OPTIONS;

  const [position, setPosition] = useState<PositionFilter>(
    requiredPositions?.length === 1 ? requiredPositions[0] : 'ALL'
  );
  const [sort, setSort] = useState<SortOption>(
    arePredictionsLocked
      ? sortOptions.find((option) => option.key === 'price') ?? sortOptions[0]
      : SORT_OPTIONS.find((option) => option.key === 'predicted-3') ??
          SORT_OPTIONS[0]
  );
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [costRange, setCostRange] = useState<[number, number]>([
    priceRange.min,
    priceRange.max
  ]);
  const [showAffordableOnly, setShowAffordableOnly] = useState(false);
  const [page, setPage] = useState(0);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
        setPage(0);
      }, SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(
    () => () => {
      debouncedSetSearch.cancel();
    },
    [debouncedSetSearch]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);
    debouncedSetSearch(value.length >= SEARCH_MIN_LENGTH ? value.trim() : '');
  };

  const handlePositionChange = (next: PositionFilter) => {
    setPosition(next);
    setPage(0);
  };

  const handleSortChange = (next: SortOption) => {
    setSort(next);
    setPage(0);
  };

  const handleCostChange = (range: [number, number]) => {
    setCostRange(range);
    setPage(0);
  };

  const handleAffordableToggle = () => {
    setShowAffordableOnly((current) => !current);
    setPage(0);
  };

  // What the user can actually spend on this pick: the projected bank once the
  // outgoing player is sold while a transfer is staged, and the raw bank when
  // simply browsing. `replacementBudget` is already that value — it collapses
  // to `bank` with nothing staged. Both the "Affordable only" filter and the
  // recommended box below are capped by it, so the two lists can't disagree
  // about what's affordable. Rounded to 1dp because the projection is summed
  // from prices and can land either side of the mark, e.g. 15.999999999999998,
  // which would otherwise exclude a player priced at the budget exactly.
  const spendableBudget = roundToOneDecimalPlace(replacementBudget ?? bank);
  const effectiveMaxPrice = showAffordableOnly
    ? Math.min(costRange[1], spendableBudget)
    : costRange[1];

  const lockedPositions = requiredPositions ?? [];
  const isPositionLocked = lockedPositions.length > 0;
  // With replacements pending across several positions the filter stays
  // interactive, restricted to those positions plus "All" (meaning all of
  // them together). With a single pending position it locks outright.
  const canPickAcrossPositions = lockedPositions.length > 1;
  const isPositionOptionEnabled = (value: PositionFilter) =>
    !isPositionLocked ||
    (canPickAcrossPositions &&
      (value === 'ALL' || lockedPositions.includes(value)));
  const effectivePosition: PositionFilter = !isPositionLocked
    ? position
    : !canPickAcrossPositions
    ? lockedPositions[0]
    : position === 'ALL' || lockedPositions.includes(position)
    ? position
    : 'ALL';

  const anchorGameweek = selectedGameweek ?? context?.nextGameweek?.number;
  const tileCount = sort.horizon ?? DEFAULT_TILE_COUNT;
  const endGameweek =
    anchorGameweek != null
      ? Math.min(anchorGameweek + tileCount - 1, MAX_GAMEWEEK)
      : undefined;

  // Navigating gameweeks swaps the whole prediction window, so any page the
  // user had reached no longer means anything.
  useEffect(() => {
    setPage(0);
  }, [anchorGameweek]);

  const {
    data,
    isLoading,
    isError,
    isPreviousData,
    fetchNextPage,
    hasNextPage: hasMoreFromServer,
    isFetchingNextPage
  } = usePlayers({
    enabled: !isAwaitingAccessToken,
    accessToken: accessToken ?? undefined,
    position: effectivePosition === 'ALL' ? undefined : effectivePosition,
    search:
      debouncedSearch.length >= SEARCH_MIN_LENGTH ? debouncedSearch : undefined,
    sortBy: sort.value,
    sortDirection: 'desc',
    minGameweek: anchorGameweek,
    maxGameweek: endGameweek,
    minPrice: costRange[0],
    maxPrice: effectiveMaxPrice,
    limit: PAGE_LENGTH
  });

  const disabledIds = useMemo(
    () => new Set(disabledPlayerIds ?? []),
    [disabledPlayerIds]
  );
  // Squad members stay in the list so they can be looked up and compared, and
  // are dimmed rather than dropped while a replacement is being picked
  // (FFH-2333). With nothing to pick there is nothing to disable, so plain
  // browsing renders them like any other row.
  const getIsRowDisabled = (player: Player) =>
    Boolean(onSelectPlayer) && disabledIds.has(player.id);

  // Viewing all pending positions together can't be expressed as a single
  // API position param, so the query fetches unfiltered and the restriction
  // is applied client-side; the backfill effect below pages forward through
  // any server pages the filter empties.
  const shouldScopeToLockedPositions =
    isPositionLocked && effectivePosition === 'ALL';
  const fetchedPlayers = (
    data?.pages.flatMap((queryPage) => queryPage.data) ?? []
  ).filter(
    (player) =>
      !shouldScopeToLockedPositions || lockedPositions.includes(player.position)
  );

  // Replacement suggestions: top affordable players in the open slot's
  // position by predicted points. Only while picking a replacement, and never
  // for non-paying members (predicted points are gated for them). With
  // replacements pending in several positions the suggestions follow the
  // position the user has narrowed to, defaulting to the first pending one.
  const isPickingReplacement = Boolean(onSelectPlayer) && isPositionLocked;
  const suggestionPosition = !isPositionLocked
    ? null
    : effectivePosition === 'ALL'
    ? lockedPositions[0]
    : effectivePosition;
  const suggestionSort =
    SORT_OPTIONS.find((option) => option.key === 'predicted-3') ??
    SORT_OPTIONS[0];
  const suggestionTileCount = suggestionSort.horizon ?? DEFAULT_TILE_COUNT;
  const suggestionEndGameweek =
    anchorGameweek != null
      ? Math.min(anchorGameweek + suggestionTileCount - 1, MAX_GAMEWEEK)
      : undefined;
  const { players: suggestedPlayers } = useTransferSuggestions({
    position: suggestionPosition,
    startGameweek: anchorGameweek ?? null,
    // Budget after the outgoing player is sold, so a like-for-like replacement
    // is affordable; the raw bank alone would starve the suggestions. Same
    // rounded value the filter uses, so a player on the budget exactly can't
    // be affordable to one list and not the other.
    bank: spendableBudget,
    // Unlike the main list these are hidden outright: a recommendation the
    // user can't act on is just noise.
    excludePlayerIds: disabledIds,
    accessToken: accessToken ?? undefined,
    enabled:
      isPickingReplacement && !arePredictionsLocked && !isAwaitingAccessToken
  });
  const hasSuggestions =
    isPickingReplacement &&
    !arePredictionsLocked &&
    suggestedPlayers.length > 0;

  // Always resort the fetched window by the displayed sort value so the
  // visible order matches the number on the right of the row. The backend
  // sortBy primarily decides which players come back; we sort the
  // accumulated slice client-side for visual consistency. Every sort ranks
  // highest first, including price, so the premium players lead the list.
  const players = [...fetchedPlayers].sort(
    (a, b) =>
      getSortDisplayValue(b, sort, anchorGameweek) -
      getSortDisplayValue(a, sort, anchorGameweek)
  );

  const pageStart = page * PAGE_LENGTH;
  const pageEnd = pageStart + PAGE_LENGTH;
  // If the locked-position scoping has emptied the visible page but the
  // server still has more data, walk forward to the next chunk that actually
  // contains players for the current page.
  useEffect(() => {
    const neededCount = pageEnd;
    if (
      players.length < neededCount &&
      Boolean(hasMoreFromServer) &&
      !isFetchingNextPage
    ) {
      void fetchNextPage();
    }
  }, [
    players.length,
    pageEnd,
    hasMoreFromServer,
    isFetchingNextPage,
    fetchNextPage
  ]);
  const pagePlayers = players.slice(pageStart, pageEnd);
  const hasNextPage = players.length > pageEnd || Boolean(hasMoreFromServer);

  // The backfill effect above hasn't necessarily started fetching yet on the
  // render where a partially-filled page first appears, so "waiting for more
  // rows" must be derived from the same condition that triggers it, not just
  // from the fetch flags.
  const isBackfillPending =
    players.length < pageEnd && Boolean(hasMoreFromServer);
  const isAwaitingMoreRows =
    isLoading || isFetchingNextPage || isBackfillPending;
  // Pad the visible page to a constant row count while rows are on the way,
  // so the list and the pagination footer never jump.
  const skeletonRowCount = isAwaitingMoreRows
    ? Math.max(PAGE_LENGTH - pagePlayers.length, 0)
    : 0;

  return (
    <div
      data-cy="player-list-panel"
      className="rounded-lg shadow-100 p-2 md:p-6 md:pb-3 h-fit"
    >
      <div className="flex justify-between mt-2 mb-4">
        <H3>Players</H3>
      </div>
      <div className="space-y-3">
        <BodyRegular>Position</BodyRegular>
        <div className="flex justify-between gap-1">
          {POSITION_OPTIONS.map((option, index) => {
            const isSelected = option.value === effectivePosition;
            // A single pending position locks the selector outright; pending
            // replacements across several positions keep "All" and the
            // relevant positions clickable so any of them can be searched.
            const isOptionDisabled = !isPositionOptionEnabled(option.value);
            return (
              <button
                type="button"
                key={option.value}
                data-cy={`player-position-select-${index}`}
                onClick={() => handlePositionChange(option.value)}
                disabled={isOptionDisabled}
                aria-pressed={isSelected}
                className={`font-open-sans text-sm leading-[18px] font-bold py-2 px-3 rounded-lg transition flex-1 text-center whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  isSelected
                    ? 'text-neutral-50 bg-primary-600'
                    : 'text-success-800 bg-neutral-100'
                } ${
                  isOptionDisabled
                    ? 'cursor-not-allowed opacity-60'
                    : 'cursor-pointer'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <label
          htmlFor="player-list-sort"
          className="text-base text-neutral-800 flex mb-1"
        >
          Sort by
        </label>
        <select
          id="player-list-sort"
          data-cy="player-list-sort"
          value={sort.key}
          onChange={(event) => {
            const next = sortOptions.find(
              (option) => option.key === event.target.value
            );
            if (next) handleSortChange(next);
          }}
          className="block w-full px-3 py-2 border border-neutral-300 rounded-lg bg-white"
        >
          {sortOptions.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>

        <div className="mb-3">
          <CostSlider
            label="Price"
            update={handleCostChange}
            defaultMin={priceRange.min}
            defaultMax={priceRange.max}
          />
        </div>

        <div className="pt-2">
          <SearchInput
            id="player-list-search"
            label="Search"
            value={searchInput}
            handleChange={handleSearchChange}
            showCloseIcon={searchInput !== ''}
            onClose={() => {
              debouncedSetSearch.cancel();
              setSearchInput('');
              setDebouncedSearch('');
              setPage(0);
            }}
            placeholder="Search by player or team name"
            showSearchIcon
          />
        </div>
      </div>

      <div className="mt-5 mb-2">
        <CheckBox
          name="affordable-filter"
          label="Affordable only"
          checked={showAffordableOnly}
          onChange={handleAffordableToggle}
        />
      </div>

      {hasSuggestions && (
        <div
          className="mt-5 mb-3 rounded-lg bg-neutral-100 p-2"
          data-cy="transfer-suggestions"
        >
          <div className="flex items-center justify-between mb-1">
            <BodySmallRegularBold>Recommended</BodySmallRegularBold>
            <CaptionBold textColor="text-neutral-600">
              {formatWindowLabel(
                anchorGameweek,
                suggestionEndGameweek,
                suggestionTileCount
              )}
            </CaptionBold>
          </div>
          {suggestedPlayers.map((player) => (
            <PlayerRow
              key={player.id}
              player={player}
              sort={suggestionSort}
              startGameweek={anchorGameweek}
              tileCount={suggestionTileCount}
              onSelectPlayer={onSelectPlayer}
              onShowPlayerInfo={onShowPlayerInfo}
              isDisabled={getIsRowDisabled(player)}
            />
          ))}
        </div>
      )}

      <div className="flex items-center gap-1 my-1">
        {onShowPlayerInfo && <span aria-hidden className={INFO_COLUMN_CLASS} />}
        <div className="flex items-center justify-between gap-2 px-1 flex-1 min-w-0">
          <CaptionBold textColor="text-neutral-600" className="flex-1 min-w-0">
            Player
          </CaptionBold>
          <CaptionBold
            textColor="text-neutral-600"
            className="w-10 text-right flex-none"
          >
            Price
          </CaptionBold>
          <CaptionBold
            textColor="text-neutral-600"
            className="text-center flex-none whitespace-nowrap"
            style={{ width: `${tileCount * FIXTURE_TILE_WIDTH_IN_PX}px` }}
          >
            {formatWindowLabel(anchorGameweek, endGameweek, tileCount)}
          </CaptionBold>
          <CaptionBold
            textColor="text-neutral-600"
            className="w-9 text-center font-bold flex-none"
          >
            {sort.abbr}
          </CaptionBold>
        </div>
      </div>

      {isError && (
        <BodySmallRegular
          textColor="text-danger-600"
          data-cy="player-list-error"
        >
          Couldn't load players. Please try again.
        </BodySmallRegular>
      )}

      {!isAwaitingMoreRows && !isError && pagePlayers.length === 0 && (
        <BodySmallRegular
          textColor="text-neutral-600"
          data-cy="player-list-empty"
        >
          No players match your filters.
        </BodySmallRegular>
      )}

      {/* While a new window loads (gameweek, sort or filter change) the
          previous rows stay visible but dimmed, so the click gives instant
          feedback without the layout moving. */}
      <div
        data-cy="player-list"
        className={classNames(
          'transition-opacity',
          isPreviousData && 'opacity-50 pointer-events-none'
        )}
      >
        {pagePlayers.map((player) => (
          <PlayerRow
            key={player.id}
            player={player}
            sort={sort}
            startGameweek={anchorGameweek}
            tileCount={tileCount}
            onSelectPlayer={onSelectPlayer}
            onShowPlayerInfo={onShowPlayerInfo}
            arePredictionsLocked={arePredictionsLocked}
            isDisabled={getIsRowDisabled(player)}
          />
        ))}
        {Array.from({ length: skeletonRowCount }, (_, index) => (
          <PlayerRowSkeleton
            key={`skeleton-${index}`}
            tileCount={tileCount}
            hasInfoColumn={Boolean(onShowPlayerInfo)}
          />
        ))}
      </div>

      <div className="flex justify-between items-center mt-2">
        <button
          type="button"
          onClick={() => setPage(0)}
          disabled={page === 0}
          className={`pointer ${page > 0 ? '' : 'text-opacity-20'} mr-4`}
          aria-label="First page"
          data-cy="player-list-first-page"
        >
          <Icon className="text-neutral-600" name="FirstPage" />
        </button>
        <div className="flex">
          <button
            type="button"
            onClick={() => page > 0 && setPage(page - 1)}
            disabled={page === 0}
            className={page > 0 ? 'pointer' : 'text-opacity-20'}
            aria-label="Previous page"
            data-cy="player-list-prev-page"
          >
            <Icon className="text-neutral-600" name="ChevronLeft" />
          </button>
          <button
            type="button"
            onClick={() => hasNextPage && setPage(page + 1)}
            disabled={!hasNextPage}
            className={hasNextPage ? 'pointer' : 'text-opacity-20'}
            aria-label="Next page"
            data-cy="player-list-next-page"
          >
            <Icon className="text-neutral-600" name="ChevronRight" />
          </button>
        </div>
        <BodySmallRegular textColor="text-neutral-600">
          Page {page + 1}
        </BodySmallRegular>
      </div>
    </div>
  );
};
